import { Component, OnDestroy, OnInit, effect, ViewChild } from '@angular/core';
import { Table, TableModule } from 'primeng/table';
import { TableComponent } from '../../../../core/components/table/table.component';
import { TableReadOnlyComponent } from '../../../../core/components/table-read-only/table-read-only.component';
import { ButtonModule } from 'primeng/button';
import { AddUnitComponent } from './add-unit/add-unit.component';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Unites } from './unites.model';
import { ReferentialsUnitsService } from './referentials-units.service';
import { GenericErrorResponse } from '../../../../core/models/generic-response.model';
import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StoreService } from '../../../../core/services/store.service';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';
import { IconFieldModule } from 'primeng/iconfield';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FilterEventService } from "../../../../core/services/filter-event.service";

const dialogConfig = {
  modal: true,
  width: '30vw',
  height: 'auto',
  maximizable: true,
  breakpoints: {
    '1199px': '60vw', // Largeur à 60% du viewport si la fenêtre fait moins de 1199px de large
    '991px': '70vw', // Largeur à 70% du viewport si la fenêtre fait moins de 991px
    '575px': '90vw', // Largeur à 90% du viewport si la fenêtre fait moins de 575px
  },
};

@Component({
  selector: 'app-referentials-units',
  standalone: true,
  imports: [TableModule, TableComponent, ButtonModule, InputTextModule, AddUnitComponent, CommonModule, FormsModule, DynamicDialogModule, ToastModule, TagModule, IconFieldModule, DropdownModule, RippleModule, StyleClassModule, ReactiveFormsModule, TooltipModule, TranslocoModule],
  providers: [DialogService, MessageService, ReferentialsUnitsService, provideTranslocoScope({ scope: 'referentiels' })],
  templateUrl: './referentials-units.component.html',
  styleUrl: './referentials-units.component.css',
})
export class ReferentialsUnitsComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private filterSubscription!: Subscription;
  @ViewChild('unitsTable') table!: Table;
  unitesResult!: Unites[];
  loading: boolean = false;

  uniteForm = this.formBuilder.group({
    uniteFormArray: this.formBuilder.array([]),
  });

  selectedUnite!: Unites;
  unite!: Unites;
  filteredUnitesData: Unites[] = [];

  errors: GenericErrorResponse[] = [];
  userHasReadWritePermission: boolean = false;

  ref: DynamicDialogRef | undefined;

  errorMessage?: string[];
  isFirstRun: boolean = true;
  lineSelected: boolean = false;

  constructor(
    private storeService: StoreService,
    private formBuilder: FormBuilder,
    private unitesServices: ReferentialsUnitsService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private filterEventService: FilterEventService,
  ) {
    effect(() => {
      const selectedContrat = this.storeService.selectedContract();
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    this.userHasReadWritePermission = this.storeService.hasAuthority('PARAMETRES_REFERENTIEL_RW');
    this.loadUnites();
    this.filterSubscription = this.filterEventService.filterEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event === 'reset') {
        this.clearFilter();
      }
    });
  }

  private clearFilter() {
    this.table.clear();
  }

  private loadUnites() {
    this.loading = true;
    this.unitesServices
      .getUnitesData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (unite) => {
          this.unitesResult = unite;
          if (!!this.uniteFormArray) {
            this.uniteFormArray.clear();
          }

          this.unitesResult.forEach((uniteLine) => {
            if (this.uniteFormArray) {
              this.uniteFormArray.push(
                this.formBuilder.group({
                  id: [uniteLine.id],
                  codeHecate: [uniteLine.codeHecate],
                  designation: [uniteLine.designation],
                  codeCourt: [uniteLine.codeCourt],
                }),
              );
            }
          });

          // Mettez à jour le tableau après chargement
          this.updateFilteredUnitesData();
        },
        error: (error: HttpErrorResponse) => this.handleHttpErrorResponse(error),
      });
  }

  get uniteFormArray() {
    return this.uniteForm.get('uniteFormArray') as FormArray;
  }

  // Transforme les FormControl en objets simples
  updateFilteredUnitesData() {
    this.filteredUnitesData = this.uniteFormArray.controls.map((control) => control.value);
  }

  editUnite(uniteGroup: FormGroup) {
    this.unite = uniteGroup.value;
  }

  saveRowUnite(uniteGroup: FormGroup) {
    let unite: Unites = uniteGroup.value;
    this.unitesServices
      .updateUnitesData(unite)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (inlineResponse) => {
          if (inlineResponse.success) {
            this.messageService.add({ severity: 'success', summary: this.translocoService.translate('summary.success'), detail: 'La ligne a été modifiée avec succès.' });
            let uniteSaved = inlineResponse.object as Unites;
            uniteGroup.setValue(uniteSaved);
          } else {
            this.errors = inlineResponse.errors.length > 0 ? inlineResponse.errors : [inlineResponse.exception];
            this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue ' + this.errors.map((e) => e.errorMsg), life: 3000 });
          }
        },
        error: (error: HttpErrorResponse) => {
          this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
        },
      });
  }

  cancelEditRowUnite(uniteGroup: FormGroup) {
    uniteGroup.setValue(this.unite);
  }

  addNewUnite() {
    this.ref = this.dialogService.open(AddUnitComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
      data: {
        uniteForm: this.uniteForm,
        userHasReadWritePermission: this.userHasReadWritePermission,
      },
    });

    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.messageService.add({ severity: 'success', summary: this.translocoService.translate('summary.success'), detail: 'La ligne a été ajoutée avec succès.', life: 3000 });
        this.loadUnites();
      }
    });
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
